import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {COHORT_METRIC_ENTRY_DEFAULT_PROPS} from 'common/report/util/cohorts/transformations/entries';
import {ProjectFeatureFlag} from 'common/report/constants';
import {featuresStore} from 'common/report/stores/features';
import {currentProjectStore} from 'common/report/stores/project-details';
import {useStore} from 'common/util/component-helpers';

import {Cohort} from '../cohorts-query-builder/models/cohort';
import {SerializedCohort} from '../cohorts-query-builder/models/types/serialized-cohort-types';
import {QueryBuilderUpdateEvent} from '../query-builder/types';
import {QueryMetricEntry, RowTypeComplexOption} from '../query-entry-section/types';
import {ResourceType} from '../property-filter-menu/models/types';
import {EventItem} from '../query-block/events-menu/types';
import {StagedCohort} from '../cohorts-query-builder/models/builder/staged-cohort';
import {SerializedStagedCohort} from '../cohorts-query-builder/models/types/serialized-staged-cohort-types';
import {template} from './template';
import css from './index.styl?inline';

interface CohortsQueryBuilderJsxProps {
  attrs: {
    cohort: SerializedCohort;
    'staged-cohort': SerializedStagedCohort;
    'project-id': number;
    'project-has-cohorts': boolean;
    'data-group-id': string;
    'support-b2b-cohorts': boolean;
    disabled: boolean;
    'track-name': string;
    'user-id': number;
    'workspace-id': number;
    'project-as-of-date': string;
    'max-data-history-days': string;
    'project-has-people': boolean;
    'hide-reset-button': boolean;
  };
  props?: EmptyObj;
}

type CohortsQueryBuilderState = EmptyObj;

export class CohortsQueryBuilder extends Component<CohortsQueryBuilderState, CohortsQueryBuilderJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<CohortsQueryBuilderJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      cohort: {
        type: `json`,
        default: null,
        description: `the serialized cohort you want this builder to render`,
      },
      /** @todo [pablo] drop this to remove backwards compatibility with site calls using staged-cohort */
      'staged-cohort': {
        type: `json`,
        default: null,
        description: `the serialized cohort you want this builder to render`,
      },
      'project-id': {type: `number`},
      'project-has-cohorts': `boolean`,
      'hide-reset-button': `boolean`,
      'data-group-id': {
        type: `string`,
        default: null,
        description: `the data group id that this cohort-builder queries with`,
      },
      'support-b2b-cohorts': `boolean`,
      disabled: {type: `boolean`, description: `whether this builder can be edited`},
      'track-name': `string`,
      'user-id': {type: `number`, description: `ID of user viewing the builder`},
      'workspace-id': {type: `number`},
      'project-has-people': `boolean`,
      'max-data-history-days': {
        type: `number`,
        description: `max days time menu can query into data history`,
      },
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {},
      helpers: {},
    };
  }

  connectedCallback() {
    super.connectedCallback();

    useStore(this, currentProjectStore);
  }

  hasNewQBEnabled() {
    return featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.QBCohorts);
  }

  hasPeopleEnabled() {
    return currentProjectStore.value?.hasIntegratedEngage;
  }

  get cohort() {
    const defaultCohort = Cohort.createCohortFromStaged(StagedCohort.deserialize(null)).serialize();
    return Cohort.parseCohort(this.attr(`cohort`) ?? defaultCohort);
  }

  get rowTypes(): RowTypeComplexOption[] {
    return [
      {
        label: COHORT_METRIC_ENTRY_DEFAULT_PROPS.event.label,
        value: ResourceType.User,
        readOnlyEvent: COHORT_METRIC_ENTRY_DEFAULT_PROPS.event as EventItem,
      },
    ];
  }

  /**
   * Updates the selected cohort with the updated groups from the qb
   */
  handleQueryUpdate(ev: QueryBuilderUpdateEvent) {
    const cohort = this.cohort;
    cohort.groups = ev.detail.entries as QueryMetricEntry[];

    // prevent an empty cohort definition from being constructed
    if (cohort.groups.length === 0) {
      cohort.groups = [COHORT_METRIC_ENTRY_DEFAULT_PROPS];
    }

    const detail = {
      cohort,
      queryReason: ev.detail.queryReason,
    };
    this.dispatchEvent(new CustomEvent(`cohortUpdated`, {detail}));
  }
}

export const CohortsLiteralBuilderEl = defineAndMakeJsxFactory(`mp-cohorts-query-builder`, CohortsQueryBuilder);
